import {
  Body1,
  Button,
  Field,
  Input,
  Spinner,
  Textarea,
  Tooltip,
  mergeClasses,
} from '@fluentui/react-components';
import {
  ArrowDownload16Filled,
  ArrowDownload16Regular,
  ArrowUpload16Regular,
  bundleIcon,
  ChevronDown16Regular,
  ChevronRight16Regular,
  Copy16Filled,
  Copy16Regular,
  Delete16Filled,
  Delete16Regular,
  Edit16Filled,
  Edit16Regular,
} from '@fluentui/react-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  COPY_TIMEOUT_MS,
  DATASET_ROLE_TOOLTIP_ANNOTATOR,
  EDIT_DESCRIPTION_ROWS,
} from '../../../../constants/index.js';
import { useInlineEdit } from '../../../../hooks/useInlineEdit/useInlineEdit.js';
import type {
  AnnotationDataEntry,
  AnnotationDataType,
  DataFile,
} from '../../../../services/dataHubService.interface.js';
import type { DataHubService } from '../../../../services/dataHubService.js';
import { withCorrelationId } from '../../../../services/usageContext.js';
import { useScenarioFactory } from '../../../../telemetry/hooks/useScenarioFactory/useScenarioFactory.js';
import { classifyScenarioError } from '../../../../telemetry/scenarioError.js';
import { ScenarioName } from '../../../../telemetry/scenarios.js';
import { useStyles } from '../DatasetAnnotationsTab.style.js';

const CopyIcon = bundleIcon(Copy16Filled, Copy16Regular) as React.FC;
const DeleteIcon = bundleIcon(Delete16Filled, Delete16Regular) as React.FC;
const EditIcon = bundleIcon(Edit16Filled, Edit16Regular) as React.FC;
const DownloadIcon = bundleIcon(
  ArrowDownload16Filled,
  ArrowDownload16Regular,
) as React.FC;

function truncateId(id: string): string {
  return id.length > 14 ? `${id.slice(0, 14)}…` : id;
}

function formatDateTime(dateStr?: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ── File panel inside expanded data row ──────────────────────────────────

interface DataRowFilePanelProps {
  entry: AnnotationDataEntry;
  dataSetId: string;
  annotationSetId: string;
  service: DataHubService;
  canEdit: boolean;
}

function DataRowFilePanel({
  entry,
  dataSetId,
  annotationSetId,
  service,
  canEdit,
}: DataRowFilePanelProps) {
  const styles = useStyles();
  const scenarioFactory = useScenarioFactory();

  const [localFiles, setLocalFiles] = useState<DataFile[]>(
    entry.dataFiles ?? [],
  );
  const [fileError, setFileError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(
    null,
  );
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const [fileUrls, setFileUrls] = useState<Record<string, string> | null>(null);

  useEffect(() => {
    let cancelled = false;
    void service
      .annotationGetFileUrls(dataSetId, annotationSetId, entry.dataId)
      .then((urls) => {
        if (!cancelled) setFileUrls(urls);
      })
      .catch(() => {
        /* file URLs are optional */
      });
    return () => {
      cancelled = true;
    };
  }, [service, dataSetId, annotationSetId, entry.dataId]);

  const handleDownload = useCallback(
    async (file: DataFile) => {
      setFileError(null);
      setDownloadingFileId(file.id);
      const scenario = scenarioFactory.newScenario(
        ScenarioName.DatalakeDownloadAnnotationFile,
      );
      try {
        const url = fileUrls?.[file.id];
        if (!url) {
          scenario.mark('download_url_requested');
          const urls = await service.annotationGetFileUrls(
            dataSetId,
            annotationSetId,
            entry.dataId,
            { extras: withCorrelationId(scenario.queryId) },
          );
          setFileUrls(urls);
          scenario.mark('download_url_received');
          const freshUrl = urls[file.id];
          if (!freshUrl) throw new Error('No download URL available');
          triggerDownload(freshUrl, file.fileName ?? file.id);
        } else {
          scenario.mark('cached_download_url_used');
          triggerDownload(url, file.fileName ?? file.id);
        }
        scenario.mark('browser_download_triggered');
        scenario.complete('success');
      } catch (err) {
        const classification = classifyScenarioError(err);
        scenario.complete(classification.status, classification.data);
        setFileError(
          err instanceof Error ? err.message : 'Failed to download file',
        );
      } finally {
        setDownloadingFileId(null);
      }
    },
    [
      service,
      dataSetId,
      annotationSetId,
      entry.dataId,
      fileUrls,
      scenarioFactory,
    ],
  );

  const handleDeleteFile = useCallback(
    async (fileId: string) => {
      setFileError(null);
      setDeletingFileId(fileId);
      const scenario = scenarioFactory.newScenario(
        ScenarioName.DatalakeDeleteAnnotationFile,
      );
      try {
        await service.annotationDeleteFile(
          dataSetId,
          annotationSetId,
          entry.dataId,
          fileId,
          { extras: withCorrelationId(scenario.queryId) },
        );
        scenario.complete('success');
        setLocalFiles((prev) => prev.filter((f) => f.id !== fileId));
      } catch (err) {
        const classification = classifyScenarioError(err);
        scenario.complete(classification.status, classification.data);
        setFileError(
          err instanceof Error ? err.message : 'Failed to delete file',
        );
      } finally {
        setDeletingFileId(null);
      }
    },
    [service, dataSetId, annotationSetId, entry.dataId, scenarioFactory],
  );

  const handleUploadSelect = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      void uploadFile(file);
    };
    input.click();

    async function uploadFile(file: File): Promise<void> {
      setIsUploading(true);
      setFileError(null);
      const scenario = scenarioFactory.newScenario(
        ScenarioName.DatalakeUploadAnnotationFile,
      );
      try {
        scenario.mark('upload_url_requested');
        const { fileId, uploadUrl } = await service.annotationCreateUploadUrl(
          dataSetId,
          annotationSetId,
          entry.dataId,
          file.name,
          { extras: withCorrelationId(scenario.queryId) },
        );
        scenario.mark('upload_url_generated');
        scenario.mark('blob_upload_started');
        await service.uploadFileToBlob(uploadUrl, file);
        scenario.mark('blob_uploaded', {
          'Scenario.FileSizeBytes': file.size,
        });
        scenario.mark('file_commit_started');
        const updated = await service.annotationCompleteUpload(
          dataSetId,
          annotationSetId,
          entry.dataId,
          {
            id: fileId,
            fileName: file.name,
            fileType: 'generic',
            fileSizeInBytes: file.size,
          },
        );
        scenario.mark('file_committed');
        scenario.complete('success');
        setLocalFiles(updated.dataFiles ?? []);
      } catch (err) {
        const classification = classifyScenarioError(err);
        scenario.complete(classification.status, classification.data);
        setFileError(
          err instanceof Error ? err.message : 'Failed to upload file',
        );
      } finally {
        setIsUploading(false);
      }
    }
  }, [service, dataSetId, annotationSetId, entry.dataId, scenarioFactory]);

  return (
    <div className={styles.filesSection}>
      <div className={styles.filesHeader}>
        <span className={styles.filesTitle}>Files ({localFiles.length})</span>
        {canEdit && (
          <Button
            appearance="primary"
            size="small"
            icon={
              isUploading ? <Spinner size="tiny" /> : <ArrowUpload16Regular />
            }
            disabled={isUploading}
            onClick={handleUploadSelect}
          >
            Upload File
          </Button>
        )}
      </div>

      {fileError !== null && (
        <Body1 className={styles.errorState}>{fileError}</Body1>
      )}

      {localFiles.length > 0 && (
        <div className={styles.fileList}>
          {localFiles.map((file) => (
            <div key={file.id} className={styles.fileItem}>
              <div className={styles.fileInfo}>
                <span className={styles.fileName}>
                  {file.fileName ?? file.id}
                </span>
                <span className={styles.fileId}>{file.id}</span>
              </div>
              <div className={styles.fileActions}>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={
                    downloadingFileId === file.id ? (
                      <Spinner size="tiny" />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  disabled={downloadingFileId === file.id}
                  aria-label={`Download ${file.fileName ?? file.id}`}
                  onClick={() => {
                    void handleDownload(file);
                  }}
                >
                  Download
                </Button>
                {canEdit && (
                  <Tooltip content="Delete file" relationship="label">
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={
                        deletingFileId === file.id ? (
                          <Spinner size="tiny" />
                        ) : (
                          <DeleteIcon />
                        )
                      }
                      disabled={deletingFileId === file.id}
                      aria-label={`Delete ${file.fileName ?? file.id}`}
                      onClick={() => {
                        void handleDeleteFile(file.id);
                      }}
                    />
                  </Tooltip>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function triggerDownload(url: string, fileName: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

// ── Main annotation data row ────────────────────────────────────────────

export interface AnnotationDataRowProps {
  entry: AnnotationDataEntry;
  dataSetId: string;
  annotationSetId: string;
  service: DataHubService;
  isDraft: boolean;
  canEdit: boolean;
  onDelete: (dataId: string) => void;
  onUpdated: (updated: AnnotationDataEntry) => void;
}

export function AnnotationDataRow({
  entry,
  dataSetId,
  annotationSetId,
  service,
  isDraft,
  canEdit,
  onDelete,
  onUpdated,
}: AnnotationDataRowProps) {
  const styles = useStyles();
  const scenarioFactory = useScenarioFactory();
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const {
    editing,
    editName,
    editDesc,
    isSaving,
    editError,
    canSave,
    setEditName,
    setEditDesc,
    startEditing,
    cancelEdit,
    save,
  } = useInlineEdit(entry.name ?? '', entry.description ?? '');

  const handleCopyRawDataId = useCallback(() => {
    const id = entry.rawDataId ?? '';
    void navigator.clipboard.writeText(id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), COPY_TIMEOUT_MS);
    });
  }, [entry.rawDataId]);

  const handleSave = useCallback(async () => {
    await save(async () => {
      const dataType = (
        entry.dataType !== undefined ? Number(entry.dataType) : 0
      ) as AnnotationDataType;
      const scenario = scenarioFactory.newScenario(
        ScenarioName.DatalakeUpdateAnnotationData,
      );
      try {
        const updated = await service.updateAnnotationData(
          dataSetId,
          annotationSetId,
          entry.dataId,
          {
            dataId: entry.dataId,
            dataType,
            name: editName.trim(),
            description: editDesc.trim() || undefined,
          },
          { extras: withCorrelationId(scenario.queryId) },
        );
        scenario.complete('success');
        onUpdated(updated);
      } catch (error) {
        const classification = classifyScenarioError(error);
        scenario.complete(classification.status, classification.data);
        throw error;
      }
    });
  }, [
    save,
    service,
    dataSetId,
    annotationSetId,
    entry.dataId,
    entry.dataType,
    editName,
    editDesc,
    onUpdated,
    scenarioFactory,
  ]);

  const formattedDate = useMemo(
    () => formatDateTime(entry.createdAt),
    [entry.createdAt],
  );

  const createdLabel =
    entry.createdAt !== undefined
      ? `Created ${formatDateTime(entry.createdAt)}`
      : null;
  const updatedLabel =
    entry.updatedAt !== undefined
      ? `Updated ${formatDateTime(entry.updatedAt)}`
      : null;

  return (
    <>
      <tr
        className={mergeClasses(
          styles.tableRow,
          isExpanded ? styles.tableRowExpanded : undefined,
        )}
      >
        <td
          className={mergeClasses(styles.tableCell, styles.chevronColumnCell)}
        >
          <Button
            appearance="subtle"
            size="small"
            className={styles.chevronButton}
            icon={
              isExpanded ? <ChevronDown16Regular /> : <ChevronRight16Regular />
            }
            onClick={() => setIsExpanded((v) => !v)}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Collapse entry' : 'Expand entry'}
          />
        </td>
        <td className={styles.tableCell}>{entry.name ?? '—'}</td>
        <td className={styles.tableCell}>{entry.category ?? '—'}</td>
        <td className={styles.tableCell}>
          <div className={styles.dataIdCell}>
            <span>{truncateId(entry.rawDataId ?? '')}</span>
            <Tooltip
              content={copied ? 'Copied!' : 'Copy raw data ID'}
              relationship="label"
            >
              <Button
                appearance="subtle"
                size="small"
                icon={<CopyIcon />}
                onClick={handleCopyRawDataId}
                aria-label="Copy raw data ID"
              />
            </Tooltip>
          </div>
        </td>
        <td className={styles.tableCell}>{formattedDate}</td>
        <td
          className={mergeClasses(styles.tableCell, styles.actionsColumnCell)}
        >
          {isDraft && canEdit ? (
            <Tooltip content="Delete entry" relationship="label">
              <Button
                appearance="subtle"
                size="small"
                icon={<DeleteIcon />}
                aria-label={`Delete ${entry.name ?? entry.dataId}`}
                onClick={() => onDelete(entry.dataId)}
              />
            </Tooltip>
          ) : !canEdit ? (
            <Tooltip
              content={DATASET_ROLE_TOOLTIP_ANNOTATOR}
              relationship="description"
            >
              <Button
                appearance="subtle"
                size="small"
                icon={<DeleteIcon />}
                aria-label={`Delete ${entry.name ?? entry.dataId}`}
                disabledFocusable
              />
            </Tooltip>
          ) : null}
        </td>
      </tr>

      {isExpanded && (
        <tr className={styles.expansionRow}>
          <td className={styles.expansionCell} colSpan={6}>
            <div className={styles.expansionPanel}>
              <div className={styles.metadataGrid}>
                <div>
                  <div className={styles.metadataLabel}>DATA ID</div>
                  <div
                    className={mergeClasses(
                      styles.metadataValue,
                      styles.monoValue,
                    )}
                  >
                    {truncateId(entry.dataId)}
                  </div>
                </div>
                <div>
                  <div className={styles.metadataLabel}>NAME</div>
                  <div className={styles.metadataValue}>
                    {entry.name ?? '—'}
                  </div>
                </div>
                <div>
                  <div className={styles.metadataLabel}>RAW DATA ID</div>
                  <div
                    className={mergeClasses(
                      styles.metadataValue,
                      styles.monoValue,
                    )}
                  >
                    {truncateId(entry.rawDataId ?? '')}
                  </div>
                </div>
                <div>
                  <div className={styles.metadataLabel}>CATEGORY</div>
                  <div className={styles.metadataValue}>
                    {entry.category ?? '—'}
                  </div>
                </div>
                <div>
                  <div className={styles.metadataLabel}>CONTRIBUTOR</div>
                  <div className={styles.metadataValue}>
                    {entry.contributor ?? '—'}
                  </div>
                </div>
              </div>

              {(createdLabel !== null || updatedLabel !== null) && (
                <div className={styles.dateRow}>
                  <div className={styles.dateGroup}>
                    {createdLabel !== null && <span>{createdLabel}</span>}
                    {updatedLabel !== null && <span>{updatedLabel}</span>}
                  </div>
                  {isDraft && canEdit ? (
                    <Button
                      appearance="transparent"
                      size="small"
                      icon={<EditIcon />}
                      onClick={startEditing}
                    >
                      Edit
                    </Button>
                  ) : isDraft && !canEdit ? (
                    <Tooltip
                      content={DATASET_ROLE_TOOLTIP_ANNOTATOR}
                      relationship="description"
                    >
                      <Button
                        appearance="transparent"
                        size="small"
                        icon={<EditIcon />}
                        disabledFocusable
                      >
                        Edit
                      </Button>
                    </Tooltip>
                  ) : null}
                </div>
              )}

              {editError !== null && (
                <Body1 className={styles.editError}>{editError}</Body1>
              )}

              {editing && (
                <div className={styles.editForm}>
                  <Field label="Name" required>
                    <Input
                      value={editName}
                      onChange={(_, d) => setEditName(d.value)}
                    />
                  </Field>
                  <Field label="Description">
                    <Textarea
                      value={editDesc}
                      onChange={(_, d) => setEditDesc(d.value)}
                      rows={EDIT_DESCRIPTION_ROWS}
                    />
                  </Field>
                  <div className={styles.editFormActions}>
                    <Button
                      appearance="primary"
                      size="small"
                      onClick={() => {
                        void handleSave();
                      }}
                      disabled={!canSave}
                    >
                      {isSaving ? 'Saving…' : 'Save'}
                    </Button>
                    <Button size="small" onClick={cancelEdit}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              <DataRowFilePanel
                entry={entry}
                dataSetId={dataSetId}
                annotationSetId={annotationSetId}
                service={service}
                canEdit={isDraft && canEdit}
              />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
